import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { type IntakeOption, type IntakeValues, type TicketIntakeSessionDto, type UpdateIntakeSessionInput, useTicketIntakeApi } from "@/api/ticket-intake";
import type { TicketDto } from "@/api/tickets";
import { RequestError } from "@/request";
import type { IntakeLookup } from "./intake-lookup";
import { useIntakeMessages } from "./intake-messages";
import {
  changeIntakeValue,
  type IntakeInputState,
  IntakeRequestSequence,
  initializeIntakeInput,
  intakeInputStatus,
  isFilled,
  migrateIntakeValues,
  normalizeIntakeRequester,
  selectIntakeCustomer,
  synchronizeIntakeInput,
  validateIntakeCategory,
  validateIntakeValues,
} from "./intake-model";

type IntakeFormValues = Omit<UpdateIntakeSessionInput, "concurrencyStamp" | "formVersionId">;
function toValues(session: TicketIntakeSessionDto): IntakeFormValues {
  return {
    categoryId: session.categoryId ?? null,
    subject: session.subject,
    body: session.body ?? "",
    requester: normalizeIntakeRequester(session.requester),
    context: session.context,
    fieldValues: session.fieldValues,
  };
}

export function useTicketIntake({
  initialContext = {},
  projectCode,
  enabled = true,
  onCreated,
}: {
  initialContext?: IntakeValues;
  projectCode?: string;
  enabled?: boolean;
  onCreated: (ticket: TicketDto) => void;
}) {
  const api = useTicketIntakeApi();
  const m = useIntakeMessages();
  const fixedProject = projectCode !== undefined;
  const projectScope = fixedProject ? projectCode.trim().toLowerCase() : null;
  const [sessionProjectScope, setSessionProjectScope] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [storedSession, setSession] = useState<TicketIntakeSessionDto | null>(null);
  const [values, setValues] = useState<IntakeFormValues | null>(() => ({
    categoryId: null,
    subject: "",
    body: "",
    requester: normalizeIntakeRequester(),
    context: initialContext,
    fieldValues: {},
  }));
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const [inputState, setInputState] = useState<IntakeInputState | null>(() => (values ? initializeIntakeInput(values) : null));
  const [completed, setCompleted] = useState(false);
  const [projectRetry, setProjectRetry] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [versionConflict, setVersionConflict] = useState(false);
  const mutation = useRef(false);
  const operation = useRef(new IntakeRequestSequence());
  const autoAttempt = useRef<string | undefined>(undefined);
  const contextRef = useRef(initialContext);
  contextRef.current = initialContext;
  const projectScopeRef = useRef(projectScope);
  projectScopeRef.current = projectScope;
  const mounted = useRef(true);
  const projects = useQuery({ queryKey: ["ticket-intake", "projects"], queryFn: ({ signal }) => api.projects(signal), enabled: enabled && !fixedProject });
  const session = fixedProject && sessionProjectScope !== projectScope ? null : storedSession;
  const categories = useQuery({
    queryKey: ["ticket-intake", "categories", session?.projectId],
    queryFn: ({ signal }) => api.categories(session?.projectId as string, signal),
    enabled: enabled && Boolean(session),
  });
  const inputStatus = intakeInputStatus(inputState, values);
  const dirty = !completed && (inputStatus.dirty || Boolean(storedSession?.attachments.length));

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const apply = useCallback((next: TicketIntakeSessionDto, initialize = false, preserveInput = false) => {
    setSession(next);
    const nextValues = toValues(next);
    setValues(preserveInput ? { ...nextValues, ...valuesRef.current } : nextValues);
    setInputState((previous) => (initialize || !previous ? initializeIntakeInput(nextValues) : synchronizeIntakeInput(previous, nextValues)));
    if (initialize) setCompleted(false);
    setErrors({});
  }, []);

  const fail = useCallback(
    (cause: unknown) => {
      if (!mounted.current) return;
      const code = cause instanceof RequestError ? (cause.code ?? "") : "";
      if (code.includes("FormVersionConflict")) setVersionConflict(true);
      setError(
        code.includes("ProviderNotConfigured") || code.includes("ProviderUnavailable") ? m.unavailable : cause instanceof Error ? cause.message : m.error,
      );
      const body =
        cause instanceof RequestError
          ? (cause.data as {
              error?: { data?: { errors?: { field: string; message: string }[] }; validationErrors?: { members: string[]; message: string }[] };
            })
          : null;
      const fieldErrors = body?.error?.data?.errors;
      if (fieldErrors) setErrors(Object.fromEntries(fieldErrors.map((item) => [item.field, item.message])));
      else if (body?.error?.validationErrors)
        setErrors(
          Object.fromEntries(
            body.error.validationErrors.flatMap((item) => (item.members ?? []).map((member) => [member.replace(/^fieldValues\./i, ""), item.message])),
          ),
        );
    },
    [m.error, m.unavailable],
  );

  const run = useCallback(
    async <T>(action: () => Promise<T>, blockInputs = true): Promise<T | undefined> => {
      if (mutation.current) return undefined;
      mutation.current = true;
      if (blockInputs) setBusy(true);
      setError(undefined);
      try {
        return await action();
      } catch (cause) {
        fail(cause);
        return undefined;
      } finally {
        mutation.current = false;
        if (mounted.current && blockInputs) setBusy(false);
      }
    },
    [fail],
  );

  const start = useCallback(
    async (projectId?: string) => {
      if (!enabled || (fixedProject ? !projectScope : !projectId) || mutation.current) return;
      const expectedScope = projectScope;
      setStarting(true);
      try {
        await run(async () => {
          const generation = operation.current.start();
          const project = fixedProject ? { projectCode: projectScope as string } : { projectId: projectId as string };
          const next = await api.createSession({ ...project, context: contextRef.current });
          if (!operation.current.isCurrent(generation) || !mounted.current || projectScopeRef.current !== expectedScope) return;
          setSessionProjectScope(expectedScope);
          apply(next, true, true);
          setVersionConflict(false);
        }, false);
      } finally {
        if (mounted.current) setStarting(false);
      }
    },
    [api, apply, enabled, fixedProject, projectScope, run],
  );
  useEffect(() => {
    if (!enabled || !fixedProject || !projectScope || session || starting || mutation.current) return;
    const attempt = JSON.stringify([projectScope, projectRetry]);
    if (autoAttempt.current === attempt) return;
    autoAttempt.current = attempt;
    void start();
  }, [enabled, fixedProject, projectRetry, projectScope, session, start, starting]);

  const retryProject = () => {
    if (starting) return;
    setError(undefined);
    setProjectRetry((attempt) => attempt + 1);
  };

  const projectError = fixedProject && !session && !starting && error ? `${m.projectStartFailed} ${error}` : undefined;
  const initializing = starting || (fixedProject && !session && !projectError);

  const setCategory = async (categoryId: string | null) => {
    if (!session || !values) return;
    const categoryError = validateIntakeCategory(categoryId, categories.isSuccess ? categories.data : undefined);
    if (categoryError) {
      setErrors((current) => ({ ...current, categoryId: m[categoryError] }));
      return;
    }
    await run(async () => {
      const form = await api.getForm(session.projectId, categoryId);
      setSession({ ...session, form });
      setValues({ ...values, categoryId, fieldValues: migrateIntakeValues(session.form.schema, form.schema, values.fieldValues, values.context) });
      setErrors({});
    });
  };

  const synchronize = async (): Promise<TicketIntakeSessionDto> => {
    if (!session || !values) throw new Error(m.start);
    if (!inputStatus.needsSynchronization && (session.form.formVersionId ?? null) === (session.formVersionId ?? null)) return session;
    const next = await api.updateSession(session.id, {
      ...values,
      requester: normalizeIntakeRequester(values.requester),
      formVersionId: session.form.formVersionId,
      concurrencyStamp: session.concurrencyStamp,
    });
    if (mounted.current) apply(next);
    return next;
  };

  const lookup: IntakeLookup = async (capability, search, signal) => {
    // Persist the selected context before querying; a lookup never receives arbitrary client parameters.
    if (mutation.current) throw new Error(m.loading);
    mutation.current = true;
    setBusy(true);
    try {
      const current = await synchronize();
      return await api.lookup(current.id, capability, { search, maxResultCount: 20 }, signal);
    } catch (cause) {
      const code = cause instanceof RequestError ? (cause.code ?? "") : "";
      if (code.includes("FormVersionConflict")) fail(cause);
      if (code.includes("ProviderNotConfigured") || code.includes("ProviderUnavailable")) throw new Error(m.unavailable);
      throw cause;
    } finally {
      mutation.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const selectCustomer = (option: IntakeOption | null) => {
    if (!values || !session) return;
    setValues({
      ...values,
      ...selectIntakeCustomer(session.form.schema, values.context, values.fieldValues, option),
    });
    setErrors({});
  };

  const submit = async () => {
    if (!session || !values) return;
    const validation = validateIntakeValues(session.form.schema, values.fieldValues, values.context, m);
    const categoryError = validateIntakeCategory(values.categoryId, categories.isSuccess ? categories.data : undefined);
    if (categoryError) validation.categoryId = m[categoryError];
    if (!values.subject.trim()) validation.subject = m.required;
    if (session.form.schema.requiresCustomer && !isFilled(values.context.customerId)) validation.customerId = m.required;
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    await run(async () => {
      // Internal synchronization is independent from the warning about unsubmitted input.
      // A clean synchronized session can be retried after its submission response is lost.
      const current = await synchronize();
      const ticket = await api.submit(current.id, current.concurrencyStamp);
      if (!mounted.current) return;
      setCompleted(true);
      onCreated(ticket);
    });
  };

  const reloadForm = async () => {
    if (!session || !values) return;
    await run(async () => {
      const form = await api.getForm(session.projectId, values.categoryId);
      const fieldValues = migrateIntakeValues(session.form.schema, form.schema, values.fieldValues, values.context);
      const latest = await api.getSession(session.id);
      const next = await api.updateSession(session.id, {
        ...values,
        requester: normalizeIntakeRequester(values.requester),
        fieldValues,
        formVersionId: form.formVersionId,
        concurrencyStamp: latest.concurrencyStamp,
      });
      apply(next);
      setVersionConflict(false);
      toast.success(m.recovered);
    });
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    await run(async () => {
      let next = await synchronize();
      for (const file of Array.from(files)) {
        next = await api.upload(next.id, file);
        if (mounted.current) apply(next);
      }
    });
  };

  const removeAttachment = async (id: string) => {
    if (!session) return;
    await run(async () => {
      const current = await synchronize();
      const next = await api.removeAttachment(current.id, id, current.concurrencyStamp);
      if (mounted.current) apply(next);
    });
  };

  const refreshAttachments = async () => {
    if (!session) return;
    await run(async () => {
      const next = await api.getSession(session.id);
      setSession((current) => (current ? { ...current, attachments: next.attachments, concurrencyStamp: next.concurrencyStamp } : next));
    });
  };

  return {
    session,
    values,
    projects,
    categories,
    busy,
    error,
    errors,
    versionConflict,
    dirty,
    fixedProject,
    projectCode: projectScope ?? projects.data?.find((project) => project.id === session?.projectId)?.code,
    initializing,
    projectError,
    retryProject,
    start,
    setCategory,
    lookup,
    selectCustomer,
    submit,
    upload,
    removeAttachment,
    refreshAttachments,
    reloadForm,
    setBasic: (key: "subject" | "body", value: string) => setValues((current) => (current ? { ...current, [key]: value } : current)),
    setRequester: (key: "name" | "phone" | "email", value: string) =>
      setValues((current) => (current ? { ...current, requester: { ...current.requester, [key]: value } } : current)),
    setField: (key: string, value: unknown) =>
      setValues((current) =>
        current && session ? { ...current, fieldValues: changeIntakeValue(session.form.schema, current.fieldValues, current.context, key, value) } : current,
      ),
  };
}
